import { PayPalCheckoutLoadPayPalSDKOptions, PayPalCheckoutTokenizationOptions } from 'braintree-web/paypal-checkout';

// Design System
import env from 'jl-design-system/utils/env/env';

// Types
import { AppDispatch } from 'types/RootState.types';
import { PayPalType, PayPalTokenizePaymentType } from './PayPalButton.types';

// Config
import featureConstants from '@constants/featureConstants';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import { hideDisableSiteSpinner, showDisableSiteSpinner } from '@redux/actions/app/appActions';
import {
  getPayPalExpressPaymentRequest,
  getPayPalPaymentRequest,
  payPalPaymentOnApprove,
  payPalPaymentOnCancel,
  payPalPaymentOnError,
  shippingAddressChange,
} from '@redux/actions/payment/payPalPaymentActions';
import { countryCodeIsInternational } from '@utils/address/addressHelpers';
import { modifyCreateOrderPayload } from '@utils/payment/modifyCreateOrderPayload';
import { getStorageItem } from '@utils/storage/storage';

export const PayPalTypes = {
  PayPal: 'paypal',
  Express: 'paypalExpress',
  PayLater: 'paypalPayLater',
};

export const paymentTypeConstantsMap: Record<PayPalType, string> = {
  paypal: paymentTypeConstants.PAYPAL,
  paypalExpress: paymentTypeConstants.PAYPAL_EXPRESS,
  paypalPayLater: paymentTypeConstants.PAYPAL_PAY_LATER,
};

export const getPayPalSdkOptions = ({ type }: { type: PayPalType }): PayPalCheckoutLoadPayPalSDKOptions => ({
  currency: 'GBP',
  intent: 'authorize',
  ...(type === PayPalTypes.PayLater && { 'enable-funding': 'paylater' }),
});

export const tokenHasJustBeenReceived = (previousState: string | undefined, currentState: string | undefined) =>
  !previousState && !!currentState;

export const isLocalPayPalSandbox = () =>
  getStorageItem(featureConstants.USE_LOCAL_PAYPAL_SANDBOX_OVERRIDE, true) === 'true';

export const resolvePayPalClientToken = ({ payPalClientToken }: { payPalClientToken?: string }) =>
  isLocalPayPalSandbox() ? getStorageItem(featureConstants.PAYPAL_CLIENT_TOKEN_OVERRIDE, true) : payPalClientToken;

export const useRealPayPal = () => (!env.isClientLocal && !env.isClientDev) || isLocalPayPalSandbox();

export const onShippingAddressChangeHandler =
  ({ dispatch, payPalCheckoutInstance }: { dispatch: AppDispatch; payPalCheckoutInstance?: any }) =>
    async (
      data: {
      errors: { ADDRESS_ERROR: string; COUNTRY_ERROR: string };
      orderID: string;
      shippingAddress: { countryCode: string };
    },
      actions: { reject: (reason: string) => void },
    ) => {
      const { shippingAddress, errors, orderID: paymentId } = data;

      if (countryCodeIsInternational(shippingAddress.countryCode)) {
        return actions.reject(errors.COUNTRY_ERROR);
      }

      try {
        const shippingAddressResponse = await dispatch(shippingAddressChange(data));
        const hasShippingFailed = shippingAddressResponse?.type?.includes('FAILED');

        if (hasShippingFailed) {
          return actions.reject(errors.ADDRESS_ERROR);
        }

        const paymentRequest = await dispatch(getPayPalExpressPaymentRequest());
        const { result } = paymentRequest;

        if (!result) {
          return actions.reject(errors.ADDRESS_ERROR);
        }

        const { amount, currency, shippingOptions } = result;

        if (payPalCheckoutInstance?.updatePayment) {
          await payPalCheckoutInstance.updatePayment({
            paymentId,
            amount,
            currency,
            shippingOptions,
          });
        } else {
        // eslint-disable-next-line no-console
          console.error('payPalCheckoutInstance is not initialized or lacks updatePayment.');
        }
      } catch (error: any) {
      // eslint-disable-next-line no-console
        console.error('Error in onShippingAddressChange:', error);
        return actions.reject(error?.code);
      }
    };

export const onErrorHandler =
  ({ dispatch }: { dispatch: AppDispatch }) =>
    async () => {
      dispatch(hideDisableSiteSpinner());
      await dispatch(payPalPaymentOnError());
    };

export const onCancelHandler =
  ({ dispatch }: { dispatch: AppDispatch }) =>
    async () => {
      dispatch(hideDisableSiteSpinner());
      await dispatch(payPalPaymentOnCancel());
    };

export const onApproveHandler =
  ({ dispatch, payPalCheckoutInstance, type }: { dispatch: AppDispatch; payPalCheckoutInstance: any; type: string }) =>
    async (data: PayPalCheckoutTokenizationOptions) => {
      dispatch(hideDisableSiteSpinner());

      const {
        details: { billingAddress, firstName, lastName, phone: phoneNumber, shippingAddress },
        nonce,
        shippingOptionId,
      } = (await payPalCheckoutInstance.tokenizePayment(data)) as PayPalTokenizePaymentType;

      const paymentData = {
        firstName,
        lastName,
        phoneNumber,
        billingAddress,
        nonce,
        ...(type === PayPalTypes.Express && { shippingAddress, shippingOptionId }),
      };

      await dispatch(payPalPaymentOnApprove(paymentData));
    };

export const onCreateOrderHandler =
  ({ dispatch, payPalCheckoutInstance, type }: { dispatch: AppDispatch; payPalCheckoutInstance: any; type: string }) =>
    async () => {
      dispatch(showDisableSiteSpinner());

      let payPalPaymentRequest;
      if (type === PayPalTypes.Express) {
        payPalPaymentRequest = await dispatch(getPayPalExpressPaymentRequest());
        payPalPaymentRequest = payPalPaymentRequest.result;

        if (payPalPaymentRequest?.shippingOptions) {
          payPalPaymentRequest = modifyCreateOrderPayload(payPalPaymentRequest);
        }
      } else {
        payPalPaymentRequest = await dispatch(getPayPalPaymentRequest());
      }

      return payPalCheckoutInstance.createPayment(payPalPaymentRequest).catch(() => {});
    };
